/**
 * Route Matching Utilities
 *
 * Centralized route matching logic using react-router's matchPath.
 * Replaces string-based matching scattered across Sidebar and routes config.
 *
 * @see .github/copilot-instructions.md#architecture-rules
 *
 * TODO: Add test coverage for route matching logic
 * - Test Reports menu activation on /reports and /reports/:tab
 * - Test Reports menu activation on /team/:teamId/* paths (team profiles)
 * - Test Reports menu NOT active on unrelated routes
 * - Test edge cases like /team without an ID
 * - Test Team Management activation on team profile pages
 * - Test pattern matching with route params
 */

import { matchPath } from 'react-router-dom';

/**
 * Route matching patterns
 * Define all route patterns in one place for consistency
 */
export const ROUTE_PATTERNS = {
  REPORTS: ['/reports', '/reports/:tab'],
  TEAMS_LEGACY: ['/teams', '/teams/:view'],
  TEAMS_MANAGEMENT: '/teams-2',
  TEAM_PROFILE: '/team/:teamId/*',
  TEAM_LIFECYCLE: '/team-lifecycle',
  INTEGRATIONS: '/integrations',
  INTEGRATION_DETAILS: '/integration/:type',
} as const;

/**
 * Check if current pathname matches any of the given patterns
 * Uses react-router's matchPath with case-sensitive and exact matching options
 */
export const matchesAnyPattern = (pathname: string, patterns: string | string[]): boolean => {
  const patternArray = Array.isArray(patterns) ? patterns : [patterns];

  return patternArray.some((pattern) => {
    // Try exact match first
    if (matchPath({ path: pattern, caseSensitive: true, end: true }, pathname)) {
      return true;
    }

    // Also check if pathname starts with pattern (for sub-routes)
    // Extract base path without params
    const basePath = pattern.split(':')[0].replace(/\/$/, '');
    if (basePath && pathname.startsWith(basePath + '/')) {
      return true;
    }

    return false;
  });
};

/**
 * Check if route should have "has-shared-root-url" class
 * Used for sidebar highlighting when multiple routes share a base path
 *
 * Note: Matching is case-sensitive to follow standard URL routing behavior
 */
export const shouldHaveSharedRootClass = (pathname: string): boolean => {
  if (!pathname) return false;

  // Reports and legacy Teams paths share visual grouping
  // matchPath is case-sensitive by default, matching real router behavior
  return matchesAnyPattern(pathname, [...ROUTE_PATTERNS.REPORTS, ...ROUTE_PATTERNS.TEAMS_LEGACY]);
};

/**
 * Check if sidebar route should be active
 * Handles special cases like Team Management being active on team profile pages
 */
export const isSidebarRouteActive = (routePath: string, currentPathname: string): boolean => {
  if (!routePath) return false;

  // Special case: Teams Management menu item should be active on team profile pages
  // Team profile pages are accessed through Teams Management section
  if (routePath === ROUTE_PATTERNS.TEAMS_MANAGEMENT) {
    return matchesAnyPattern(currentPathname, [
      ROUTE_PATTERNS.TEAMS_MANAGEMENT,
      ROUTE_PATTERNS.TEAM_PROFILE,
    ]);
  }

  // Handle route params (e.g., /teams/:view -> match /teams/*)
  // Extract base path before any param segments
  const basePath = routePath.split(':')[0].replace(/\/$/, '');

  // Try exact match first
  if (currentPathname === routePath || currentPathname === basePath) {
    return true;
  }

  // Try pattern match for parameterized routes
  if (matchPath({ path: routePath, caseSensitive: true, end: false }, currentPathname)) {
    return true;
  }

  // Try prefix match for nested routes
  return currentPathname.startsWith(basePath + '/');
};

/**
 * Get the effective base path for a route
 * Strips parameter segments for comparison
 */
export const getRouteBasePath = (routePath: string): string => {
  return routePath.split(':')[0].replace(/\/$/, '');
};
